import PropTypes from 'prop-types';
import React, { useCallback, useContext, useEffect } from 'react';
import styled from 'styled-components';

// components
import CartItemMembershipDonations from './CartItemMembershipDonations';
import CartItemMembershipPrices from './CartItemMembershipPrices';
import PillToggle from '@atoms/PillToggle';
import NotificationBanner, { notificationTypes } from '@molecules/NotificationBanner';
import { BodyMedium, LabelSmall } from '@styled';

// functionality
import { CartItemMembershipContext } from './CartItemMembershipProvider';
import { useDeleteCartItemModal } from '../../../../modals/DeleteCartItemModal';
import { LoadingContext } from '@context/LoadingProvider';
import { deleteCartItem, replaceCartItem } from '@general/api/cartAPI';
import { processCartErrors } from '@general/helpers/cart.helpers';
import colors from '@general/colors';
import productTypes from '@general/productTypes';
import useCartFromAPI from '@hooks/apiLegacy/useCartFromAPI';
import useMemberDuesEligibilityFromAPI from '@hooks/apiLegacy/useMemberDuesEligibilityFromAPI';
import useMembershipDuesYears from '@hooks/useCartDuesYears';
import { useScreenMinMd, useScreenSmDown } from '@hooks/useResponsive';
import useViewer from '@hooks/useViewer';
import useCartClearFlashNoticesUpdateCart from '@hooks/useCartClearFlashNoticesUpdateCart';

const RemoveLinkStyled = styled(LabelSmall)`
    color: ${colors.azure};
    text-decoration: underline;
`;

const HeaderElements = ({ isLoading, membershipCartItem, onRemoveClick, setIsLoading }) => {
    const { clearFlashNoticesRefetchCart } = useCartClearFlashNoticesUpdateCart();

    return (
        <div>
            <BodyMedium>{membershipCartItem.description}</BodyMedium>
            <RemoveLinkStyled
                as="a"
                href="#"
                className="mt-0.5rem"
                onClick={e => {
                    e.preventDefault();
                    onRemoveClick();
                }}
            >
                Remove
            </RemoveLinkStyled>

            {!isLoading && membershipCartItem.meta_info?.flash_notice && (
                <div className="mt-0.5rem">
                    <NotificationBanner
                        handleDismiss={async () => {
                            setIsLoading(true);
                            await clearFlashNoticesRefetchCart();
                            setIsLoading(false);
                        }}
                        notificationType={notificationTypes.ERROR}
                    >
                        {membershipCartItem.meta_info.flash_notice}
                    </NotificationBanner>
                </div>
            )}
        </div>
    );
};

HeaderElements.propTypes = {
    isLoading: PropTypes.bool,
    membershipCartItem: PropTypes.object.isRequired,
    onRemoveClick: PropTypes.func.isRequired,
    setIsLoading: PropTypes.func,
};

const CartItemMembership = ({ onComponentLoaded = () => {} }) => {
    // -----------------------------
    // Hooks
    // -----------------------------

    const { membershipDataForMember, membershipCartItem } = useContext(CartItemMembershipContext);
    const { isLoading, setIsLoading } = useContext(LoadingContext);
    const isScreenMinMd = useScreenMinMd();
    const isScreenSmDown = useScreenSmDown();
    const { viewer } = useViewer();
    const { cart, refetch } = useCartFromAPI({ viewerId: viewer?.recordId });
    const { memberDuesEligibility } = useMemberDuesEligibilityFromAPI({ id: membershipDataForMember.memberId });
    const { openModal: openDeleteConfirmationModal } = useDeleteCartItemModal();
    const { oneOrThreeYears } = useMembershipDuesYears({
        memberId: membershipDataForMember?.memberId,
        duesProductId: membershipCartItem?.product_id,
    });

    // -----------------------------
    // Effects
    // -----------------------------

    useEffect(() => {
        if (getIsReadyForRender()) {
            onComponentLoaded();
        }
    }, [getIsReadyForRender, onComponentLoaded, memberDuesEligibility, oneOrThreeYears]);

    // -----------------------------
    // Helpers
    // -----------------------------

    const getIsReadyForRender = useCallback(() => {
        if (!memberDuesEligibility || !oneOrThreeYears) {
            return false;
        }

        return true;
    }, [memberDuesEligibility, oneOrThreeYears]);

    const removeMembershipItem = () => {
        const duesItem = membershipDataForMember.dues.find(
            cartItem => cartItem.product_type === productTypes.MEMBERSHIP
        );

        openDeleteConfirmationModal(`${duesItem.description} for ${duesItem.meta_info.member_name}`, async () => {
            setIsLoading(true);

            const cartData = await membershipDataForMember.dues.reduce(async (promise, dues) => {
                await promise;
                return deleteCartItem({
                    cartId: cart.id,
                    cartItemId: dues.id,
                    viewerId: viewer.recordId,
                });
            }, Promise.resolve());

            await processCartErrors({ cart: cartData, viewer });
            await refetch();
            setIsLoading(false);
        });
    };

    const replaceDuesItem = async years => {
        const duesToUse = memberDuesEligibility.dues.find(dues => dues.years === years);
        const newDuesItem = { ...membershipCartItem, ...duesToUse };
        newDuesItem.product_id = duesToUse.id;
        delete newDuesItem.id;

        try {
            return replaceCartItem({
                cartId: cart.id,
                cartItemId: membershipCartItem.id,
                cartItemData: newDuesItem,
                viewerId: viewer.recordId,
            });
        } catch {
            alert('Error toggling years');
        }
    };

    const replaceSicItem = async years => {
        const sicInCart = cart.items.find(
            item => item.member_id === membershipDataForMember.memberId && item.product_type === productTypes.SIC
        );

        if (!sicInCart || !memberDuesEligibility.sic) {
            return;
        }

        const sicToUse = memberDuesEligibility.sic.find(sic => sic.years === years);
        const newSicItem = { ...sicInCart, ...sicToUse };
        newSicItem.product_id = sicToUse.id;
        delete newSicItem.id;

        try {
            await replaceCartItem({
                cartId: cart.id,
                cartItemId: sicInCart.id,
                cartItemData: newSicItem,
                viewerId: viewer.recordId,
            });
        } catch {
            alert('Error toggling years');
        }
    };

    const replacePacItem = async years => {
        const pacInCart = cart.items.find(
            item => item.member_id === membershipDataForMember.memberId && item.product_type === productTypes.PAC
        );

        if (!pacInCart || !memberDuesEligibility.pac) {
            return;
        }

        const isPacCorporate = !!pacInCart.meta_info.corporate;
        const pacToUse = memberDuesEligibility.pac.find(pac => pac.years === years && pac.corporate === isPacCorporate);
        const newPacItem = { ...pacInCart, ...pacToUse };
        newPacItem.product_id = pacToUse.id;
        delete newPacItem.id;

        try {
            await replaceCartItem({
                cartId: cart.id,
                cartItemId: pacInCart.id,
                cartItemData: newPacItem,
                viewerId: viewer.recordId,
            });
        } catch {
            alert('Error toggling years');
        }
    };

    const replaceFndItem = async years => {
        const fndInCart = cart.items.find(
            item =>
                item.member_id === membershipDataForMember.memberId &&
                item.product_type === productTypes.FOUNDATION_DONATION
        );

        if (!fndInCart || !memberDuesEligibility.fnd) {
            return;
        }

        const fndToUse = memberDuesEligibility.fnd.find(fnd => fnd.years === years);
        const newFndItem = { ...fndInCart, ...fndToUse };
        newFndItem.product_id = fndToUse.id;
        delete newFndItem.id;

        try {
            await replaceCartItem({
                cartId: cart.id,
                cartItemId: fndInCart.id,
                cartItemData: newFndItem,
                viewerId: viewer.recordId,
            });
        } catch {
            alert('Error toggling years');
        }
    };

    const toggleMembershipYears = async years => {
        if (oneOrThreeYears === years) {
            return;
        }

        setIsLoading(true);
        await replaceDuesItem(years);
        await replaceSicItem(years);
        await replacePacItem(years);
        await replaceFndItem(years);
        await refetch();
        setIsLoading(false);
    };

    // -----------------------------
    // Render
    // -----------------------------

    if (!getIsReadyForRender()) {
        return null;
    }

    return (
        <div>
            <div className="flex items-start">
                {isScreenMinMd && (
                    <div className="mr-1.875rem">
                        <img src="/assets/images/ecomm/cart-item-membership-icon.png" alt="Membership" />
                    </div>
                )}

                <div className="flex-1">
                    <PillToggle
                        activeInitial={oneOrThreeYears === 1 ? 'left' : 'right'}
                        leftLabel="1 Year"
                        rightLabel="3 Year"
                        onLeftClick={() => toggleMembershipYears(1)}
                        onRightClick={() => toggleMembershipYears(3)}
                    />

                    {isScreenMinMd && (
                        <div className="mt-1rem">
                            <HeaderElements
                                isLoading={isLoading}
                                membershipCartItem={membershipCartItem}
                                onRemoveClick={() => removeMembershipItem()}
                                setIsLoading={setIsLoading}
                            />
                        </div>
                    )}
                </div>

                <div className="ml-1.5rem">
                    <CartItemMembershipPrices locale={cart.locale} />
                </div>
            </div>

            {isScreenSmDown && (
                <div className="mt-1rem">
                    <HeaderElements
                        isLoading={isLoading}
                        membershipCartItem={membershipCartItem}
                        onRemoveClick={() => removeMembershipItem()}
                        setIsLoading={setIsLoading}
                    />
                </div>
            )}

            <div style={{ marginLeft: isScreenMinMd ? '76px' : 0 }}>
                <CartItemMembershipDonations />
            </div>
        </div>
    );
};

CartItemMembership.propTypes = {
    onComponentLoaded: PropTypes.func,
};

export default CartItemMembership;
